// kitchen/web/src/views/HistoryView.tsx
// Feature 148 (US2) — Vista "Storico" del cuoco (sola consultazione).
// Mostra ciò che è servito/completato nella giornata corrente (COMPLETED,
// CANCELLED e ondate SERVED di ordini ancora attivi — Q3=B), con note/modifiche/
// variante per ogni piatto. Snapshot on-demand: si carica all'apertura e si
// aggiorna solo col pulsante "Aggiorna" (FR-010a). NON tocca lo store/cache della
// vista live (query isolata in useHistoryOrders) → zero interferenza (FR-008).
//
// KDS su schermo touch (TouchWo): NIENTE swipe/rotella → lo scroll verticale
// avviene con i bottoni ▲▼ (stesso pattern delle frecce di TableBoard).

import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { RotateCw, ChevronUp, ChevronDown } from "lucide-react";
import { useHistoryOrders } from "../hooks/useOrders";
import { useClientConfig } from "../hooks/useClientConfig";
import {
  selectHistory,
  filterCurrentBusinessDay,
  type HistoryEntry,
} from "@shared/kds/history-filter";
import { useTranslation } from "../i18n";
import type { KdsT } from "../lib/i18n/settings";

// Cutoff giornata lavorativa di default (ora Rome) finché il backend non espone
// `businessDayCutoffHour` nel client-config. 4 = le 04:00 (Nereide).
const DEFAULT_BUSINESS_DAY_CUTOFF_HOUR = 4;

interface HistoryViewProps {
  clientId: string;
}

const STATUS_META: Record<
  HistoryEntry["status"],
  { label: (t: KdsT) => string; className: string }
> = {
  served: { label: (t) => t("kds.status.served", "Servito"), className: "bg-kds-served/20 text-kds-served" },
  completed: { label: (t) => t("kds.status.completed", "Completato"), className: "bg-kds-ready/20 text-kds-ready" },
  cancelled: {
    label: (t) => t("kds.status.cancelled", "Annullato"),
    className: "bg-kds-cancelled/20 text-kds-cancelled",
  },
};

export default function HistoryView({ clientId }: HistoryViewProps) {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, refetch } = useHistoryOrders(
    clientId,
    true,
  );

  const { clientConfig } = useClientConfig(clientId);
  const cutoffHour =
    clientConfig?.businessDayCutoffHour ?? DEFAULT_BUSINESS_DAY_CUTOFF_HOUR;

  // Lo Storico carica 2 bucket UTC (oggi+ieri) per non perdere nulla al confine
  // di mezzanotte UTC, poi filtra alla giornata di servizio corrente (cutoff
  // business-day, es. 04:00 Rome). Ricalcolato a ogni fetch/refresh.
  const entries = useMemo(() => {
    const all = selectHistory(data ?? [], t);
    return filterCurrentBusinessDay(all, cutoffHour, Date.now());
  }, [data, cutoffHour, t]);

  // Scroll verticale a bottoni (touch screen, niente swipe).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 4);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  // NB: dipende da entries.length perché il div scrollabile è montato solo
  // quando ci sono elementi (durante il caricamento scrollRef.current è null).
  // Senza questa dipendenza il listener non si aggancerebbe mai al contenitore
  // reale → canScrollUp resterebbe sempre false (bottone su sempre disabilitato).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, entries.length]);

  const scrollByPage = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * el.clientHeight * 0.85, behavior: "smooth" });
  };

  const activeBtn =
    "bg-red-600 text-white border-2 border-red-300 shadow-xl active:scale-95";
  const disabledBtn =
    "bg-kds-card/60 text-kds-muted border-2 border-kds-muted/20 cursor-default";

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-4">
      {/* Header vista storico + Aggiorna (fisso) */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h2 className="text-kds-text text-xl font-bold">{t("kds.history.title", "Storico della giornata")}</h2>
          <p className="text-kds-muted text-sm">
            {t("kds.history.subtitle", "Ordini e ondate già serviti o completati oggi")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 rounded-lg bg-kds-card px-4 py-2 text-kds-text hover:brightness-125 disabled:opacity-50 transition"
          aria-label={t("kds.history.refreshAria", "Aggiorna lo storico")}
        >
          <RotateCw
            size={16}
            className={isFetching ? "animate-spin" : undefined}
          />
          {t("kds.common.refresh", "Aggiorna")}
        </button>
      </div>

      {isLoading ? (
        <p className="text-kds-muted text-center py-10">{t("kds.common.loading", "Caricamento…")}</p>
      ) : entries.length === 0 ? (
        <p className="text-kds-muted text-center py-10">
          {t("kds.history.empty", "Nessun piatto servito o completato in questa giornata.")}
        </p>
      ) : (
        // Wrapper relativo: bottoni scroll ▲▼ assoluti sopra l'area scrollabile.
        // min-h-0 CRITICO altrimenti il flex item cresce col contenuto e le card
        // escono dallo schermo invece di scrollare.
        <div className="relative flex-1 min-h-0">
          <div ref={scrollRef} className="h-full overflow-y-auto pr-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-24">
              {entries.map((entry) => {
                const meta = STATUS_META[entry.status];
                return (
                  <div
                    key={entry.orderId}
                    className={`rounded-xl bg-kds-surface border p-3 ${
                      entry.status === "cancelled"
                        ? "border-kds-cancelled/40"
                        : "border-white/5"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-kds-text font-semibold">
                        {entry.tableLabel ?? "—"}
                        {entry.dailyNumber != null && (
                          <span className="text-kds-muted font-normal">
                            {" "}
                            · #{entry.dailyNumber}
                          </span>
                        )}
                      </div>
                      <span
                        className={`text-[11px] font-bold uppercase tracking-wide rounded px-2 py-0.5 ${meta.className}`}
                      >
                        {meta.label(t)}
                      </span>
                    </div>

                    <ul className="space-y-2">
                      {entry.items.map((it) => (
                        <li
                          key={it.id}
                          className={`text-sm ${
                            it.status === "CANCELLED" ? "opacity-60" : ""
                          }`}
                        >
                          <div className="text-kds-text font-medium">
                            {it.quantity}× {it.dishName}
                            {it.serviceWave != null && (
                              <span className="text-kds-muted font-normal">
                                {" "}
                                · {t("kds.history.wave", "onda {{wave}}", { wave: it.serviceWave })}
                              </span>
                            )}
                          </div>
                          {it.variantName && (
                            <div className="text-kds-text">◆ {it.variantName}</div>
                          )}
                          {it.modifications && (
                            <div className="text-kds-preparing whitespace-pre-line">
                              ⚠ {it.modifications}
                            </div>
                          )}
                          {it.notes && (
                            <div className="text-kds-text whitespace-pre-line">
                              {it.notes}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottoni scroll ▲▼ — sempre montati (attivi rossi con overflow,
              spenti grigi altrimenti), touch-friendly. */}
          <button
            type="button"
            aria-label={t("kds.history.scrollUp", "Scorri su")}
            disabled={!canScrollUp}
            onClick={() => scrollByPage(-1)}
            className={[
              "absolute right-3 top-3 z-20 flex items-center justify-center",
              "w-16 h-16 rounded-full",
              canScrollUp ? activeBtn : disabledBtn,
            ].join(" ")}
          >
            <ChevronUp size={34} strokeWidth={3} />
          </button>
          <button
            type="button"
            aria-label={t("kds.history.scrollDown", "Scorri giù")}
            disabled={!canScrollDown}
            onClick={() => scrollByPage(1)}
            className={[
              "absolute right-3 bottom-3 z-20 flex items-center justify-center",
              "w-16 h-16 rounded-full",
              canScrollDown ? activeBtn : disabledBtn,
            ].join(" ")}
          >
            <ChevronDown size={34} strokeWidth={3} />
          </button>
        </div>
      )}
    </div>
  );
}
